/**
 * Two-Tier Insights Panel
 * Displays dynamic analytics: hot/cold players, rolling windows, prop trends, streaks
 *
 * All data is fetched from the database - nothing is hardcoded.
 */
import { formatPercent, formatROI, formatStreak } from '@parlay-picker/shared/formatting';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type { ReactElement } from 'react';
import type {
  HotColdPlayer,
  PropTypeTrend,
  RollingWindowPerformance,
  TwoTierAnalytics,
} from '../hooks/useLabDashboard';

function getTrendColor(trend: 'hot' | 'cold' | 'neutral'): string {
  switch (trend) {
    case 'hot':
      return 'text-theme-success';
    case 'cold':
      return 'text-theme-danger';
    default:
      return 'text-theme-text-secondary';
  }
}

function getTrendBg(trend: 'hot' | 'cold' | 'neutral'): string {
  switch (trend) {
    case 'hot':
      return 'bg-theme-success/15';
    case 'cold':
      return 'bg-theme-danger/15';
    default:
      return 'bg-theme-surface/50';
  }
}

// =============================================================================
// Sub-Components
// =============================================================================

function HotColdPlayersCard({
  hot,
  cold,
  recentDays,
}: {
  hot: HotColdPlayer[];
  cold: HotColdPlayer[];
  recentDays: number;
}): ReactElement {
  return (
    <div className="bg-theme-surface/50 rounded-lg p-4">
      <h3 className="text-sm font-medium text-theme-text-secondary mb-3 shrink-0">
        Hot & Cold Players (Last {recentDays}d)
      </h3>

      <div className="grid @3xl:grid-cols-2 gap-4">
        {/* Hot Players */}
        <div className="flex flex-col max-h-60">
          <div className="text-xs text-theme-success font-medium mb-2 flex items-center gap-1 shrink-0">
            <span className="w-2 h-2 bg-theme-success rounded-full" />
            Hot Players
          </div>
          {hot.length === 0 ? (
            <div className="text-xs text-theme-text-tertiary">No hot players found</div>
          ) : (
            <div className="space-y-1.5 overflow-auto flex-1">
              {hot.map((player, idx) => (
                <div
                  key={`${player.playerName}-${player.propType}-${idx}`}
                  className="flex items-center justify-between text-xs bg-theme-success/10 rounded px-2 py-1.5"
                >
                  <div>
                    <span className="text-theme-text">{player.playerName}</span>
                    {player.propType && (
                      <span className="text-theme-text-tertiary ml-1">
                        ({formatStatLabel(player.propType)})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-theme-success font-medium">
                      {formatPercent(player.recentHitRate)}
                    </span>
                    <span className="text-green-300 text-[10px]">
                      {formatStreak(player.streak)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cold Players */}
        <div className="flex flex-col max-h-60">
          <div className="text-xs text-theme-danger font-medium mb-2 flex items-center gap-1 shrink-0">
            <span className="w-2 h-2 bg-theme-danger rounded-full" />
            Cold Players
          </div>
          {cold.length === 0 ? (
            <div className="text-xs text-theme-text-tertiary">No cold players found</div>
          ) : (
            <div className="space-y-1.5 overflow-auto flex-1">
              {cold.map((player, idx) => (
                <div
                  key={`${player.playerName}-${player.propType}-${idx}`}
                  className="flex items-center justify-between text-xs bg-theme-danger/10 rounded px-2 py-1.5"
                >
                  <div>
                    <span className="text-theme-text">{player.playerName}</span>
                    {player.propType && (
                      <span className="text-theme-text-tertiary ml-1">
                        ({formatStatLabel(player.propType)})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-theme-danger font-medium">
                      {formatPercent(player.recentHitRate)}
                    </span>
                    <span className="text-red-300 text-[10px]">{formatStreak(player.streak)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RollingWindowsCard({ windows }: { windows: RollingWindowPerformance[] }): ReactElement {
  return (
    <div className="bg-theme-surface/50 rounded-lg p-4">
      <h3 className="text-sm font-medium text-theme-text-secondary mb-3">
        Performance by Time Window
      </h3>

      {windows.length === 0 ? (
        <div className="text-xs text-theme-text-tertiary">No data available</div>
      ) : (
        <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-3">
          {windows.map((w) => (
            <div key={w.window} className="bg-theme-surface-hover/50 rounded p-3 text-center">
              <div className="text-xs text-theme-text-tertiary uppercase mb-1">
                {w.window === 'all' ? 'All Time' : w.window}
              </div>
              <div className="text-lg font-bold text-white">{formatPercent(w.hitRate)}</div>
              <div className="text-xs text-theme-text-secondary">
                {w.hits}W - {w.misses}L
              </div>
              <div
                className={`text-xs mt-1 ${w.roi >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
              >
                {formatROI(w.roi)} ROI
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PropTypeTrendsCard({ trends }: { trends: PropTypeTrend[] }): ReactElement {
  return (
    <div className="bg-theme-surface/50 rounded-lg p-4">
      <h3 className="text-sm font-medium text-theme-text-secondary mb-3">
        Prop Type Trends (7d vs Overall)
      </h3>

      {trends.length === 0 ? (
        <div className="text-xs text-theme-text-tertiary">No data available</div>
      ) : (
        <div className="grid grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4 gap-2">
          {trends.map((trend) => (
            <div key={trend.propType} className={`rounded p-2 ${getTrendBg(trend.trend)}`}>
              <div
                className="text-xs text-theme-text-secondary font-medium truncate"
                title={formatStatLabel(trend.propType)}
              >
                {formatStatLabel(trend.propType)}
              </div>
              <div className="flex items-center justify-between mt-1">
                <div className="text-xs">
                  <span className="text-theme-text-tertiary">7d: </span>
                  <span className={getTrendColor(trend.trend)}>
                    {formatPercent(trend.recentHitRate)}
                  </span>
                </div>
                <div className="text-xs">
                  <span className="text-theme-text-tertiary">All: </span>
                  <span className="text-theme-text-secondary">
                    {formatPercent(trend.overallHitRate)}
                  </span>
                </div>
              </div>
              {trend.trendDelta !== 0 && (
                <div className={`text-[10px] mt-1 ${getTrendColor(trend.trend)}`}>
                  {trend.trendDelta > 0 ? '+' : ''}
                  {(trend.trendDelta * 100).toFixed(1)}%
                  {trend.trend === 'hot'
                    ? ' trending up'
                    : trend.trend === 'cold'
                      ? ' trending down'
                      : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface TwoTierInsightsPanelProps {
  data: TwoTierAnalytics;
  loading: boolean;
  recentDays: number;
  setRecentDays: (days: number) => void;
}

export function TwoTierInsightsPanel({
  data,
  loading,
  recentDays,
  setRecentDays,
}: TwoTierInsightsPanelProps): ReactElement {
  if (loading) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-primary mx-auto mb-2" />
        <p className="text-theme-text-secondary text-sm">Loading insights...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section Header with Controls */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Dynamic Insights</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-theme-text-tertiary">Recent window:</span>
          <select
            value={recentDays}
            onChange={(e) => setRecentDays(parseInt(e.target.value, 10))}
            className="bg-theme-surface-hover text-theme-text-secondary text-xs rounded px-2 py-1 border border-theme-border"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
      </div>

      {/* Rolling Windows */}
      <RollingWindowsCard windows={data.rollingWindows} />

      {/* Hot/Cold Players */}
      <HotColdPlayersCard
        hot={data.hotColdPlayers.hot}
        cold={data.hotColdPlayers.cold}
        recentDays={recentDays}
      />

      {/* Prop Type Trends */}
      <PropTypeTrendsCard trends={data.propTypeTrends} />
    </div>
  );
}
